import Button from '@ui/button';
import { type FC, memo } from 'react';
import styles from './swap-submit.module.scss';
import { useAddress } from '@hooks/use-address';
import { walletConnectModel } from '@models/wallet-connect.model';
import { useTransaction } from '@hooks/use-transaction';
import { TransactionStatus } from '@constants/transaction-status';
import { globalSettingModel } from '@models/global-setting.model';
import { transactionModel } from '@models/transaction.model';

import type { BestSwapResult, ParrotCoinStruct } from 'src/types';
import { useModel } from 'foca';
import { BN, Decimal, TransactionUtil } from 'parrot-sui-sdk';
import { bigNumberToReadable } from '@utils/big-number-to-readable';
import parrotSdk from '@libs/parrot-sdk';
import { useParrotBalance } from '@hooks/use-parrot-balance';

interface OwnProps {
  fetchLoading: boolean;
  bestRoute: BestSwapResult | undefined;
  coinA: ParrotCoinStruct | undefined;
  coinB: ParrotCoinStruct | undefined;
  amount: string | undefined;
  onTransacted(): void;
  isLiquidity: boolean;
  isRoute: boolean;
}

const SwapSubmit: FC<OwnProps> = (props) => {
  const { bestRoute, coinA, coinB, amount, onTransacted, isLiquidity, isRoute, fetchLoading } =
    props;
  const { status: transactionStatus } = useModel(transactionModel);
  const transact = useTransaction('swap');
  const { slippage } = useModel(globalSettingModel);
  const address = useAddress();
  const { balance: balanceA } = useParrotBalance(coinA?.type);

  let msg = 'Swap';
  let disabled = false;
  let loading = false;
  let clickEvent: (() => void) | undefined;

  if (!address) {
    msg = 'Connect Wallet';
    clickEvent = walletConnectModel.toggleDialog;
  } else if (!coinA || !coinB) {
    msg = 'Select token';
    disabled = true;
  } else if (fetchLoading) {
    msg = 'Computing...';
    loading = true;
  } else if (isRoute) {
    msg = `Route doesn't exist!`;
    disabled = true;
  } else if (!amount || !balanceA || !bestRoute) {
    disabled = true;
  } else if (isLiquidity) {
    msg = `Not enough liquidity`;
    disabled = true;
  } else if (new Decimal(bestRoute.amount_in).greaterThan(balanceA)) {
    msg = `Not enough ${coinA.symbol}`;
    disabled = true;
  } else {
    clickEvent = async () => {
      const ok = await transact(
        <>
          Traded{' '}
          <b>
            {bigNumberToReadable(bestRoute.amount_in, coinA.decimals)} {coinA.symbol}
          </b>{' '}
          for{' '}
          <b>
            {bigNumberToReadable(bestRoute.amount_out, coinB.decimals)} {coinB.symbol}
          </b>
        </>,
        async () => {
          parrotSdk.senderAddress = address;
          console.log({
            paths: bestRoute.paths.map((path) => ({
              ...path,
              amount_in: path.amount_in,
              amount_out: path.amount_out,
              raw_amount_limit: path.raw_amount_limit
                .filter((item) => item)
                .map((item) => (item ? item : undefined)),
            })),
            partner: '',
            price_slippage_point: Number(slippage),
          });
          const allCoinAsset = await parrotSdk.Coin.getOwnerCoinAssets(address);
          return TransactionUtil.buildRouterSwapTransaction(
            parrotSdk,
            {
              paths: bestRoute.paths.map((path) => ({
                ...path,
                amount_in: new BN(path.amount_in),
                amount_out: new BN(path.amount_out),
                raw_amount_limit: path.raw_amount_limit
                  .filter((item) => item)
                  .map((item) => (item ? new BN(item) : undefined)),
              })),
              partner: '',
              price_slippage_point: Number(slippage),
            },
            bestRoute.by_amount_in,
            allCoinAsset,
          );
        },
      );

      ok.status && onTransacted();
    };
  }

  return (
    <Button
      disabled={disabled}
      className={styles.button}
      loading={loading || transactionStatus === TransactionStatus.confirm}
      onClick={clickEvent}
    >
      {transactionStatus === TransactionStatus.confirm ? 'Loading...' : msg}
    </Button>
  );
};

export default memo(SwapSubmit);
